import React from 'react';
import {Box, Drawer, useMediaQuery, useTheme} from '@mui/material';
import {styled} from '@mui/material/styles';
import {grey} from '@mui/material/colors';
import {addShouldNotForwardProps} from '../../../utils/addShouldNotForwardProps';
import Lottie from 'react-lottie-player/dist/LottiePlayerLight';
import ArrowJSON from '../../../assets/json/arrow.json';

export const TOUR_DRAWER_WIDTH = '250px';
export const TOUR_DRAWER_HEIGHT = '400px';

interface TourDrawerProps {
  liveViewing?: boolean;
  keepDrawerOpen?: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  drawerRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

const DrawerHandle = styled(Box, {
  shouldForwardProp: addShouldNotForwardProps(
    '$drawerIsOpen',
    '$liveViewing',
    '$drawerHeight',
    '$isMobile'
  ),
})<{
  $drawerIsOpen: boolean;
  $liveViewing: boolean;
  $drawerHeight: number;
  $isMobile: boolean;
}>(({theme, $drawerIsOpen, $liveViewing, $drawerHeight, $isMobile}) => ({
  position: 'absolute',
  zIndex: 1001,
  cursor: 'pointer',
  transition: theme.transitions.create(
    ['right', 'bottom', 'margin-right', 'margin-bottom'],
    {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.sharp,
    }
  ),
  ...($isMobile
    ? {
        left: '50%',
        bottom: $drawerIsOpen
          ? `${$drawerHeight}px`
          : $liveViewing
          ? '55px'
          : theme.spacing(1),
        transform: 'translateX(-50%)',
      }
    : {
        top: '50%',
        right: $drawerIsOpen ? TOUR_DRAWER_WIDTH : theme.spacing(1),
        transform: $liveViewing ? 'translateY(-90%)' : 'translateY(-50%)',
      }),
}));

const DrawerContent = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : grey[800],
  padding: '15px 15px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: TOUR_DRAWER_WIDTH,
  borderTopLeftRadius: '8px',
  borderBottomLeftRadius: '8px',
  [theme.breakpoints.down('md')]: {
    borderBottomLeftRadius: 0,
    borderTopRightRadius: '8px',
    height: '100%',
    maxHeight: TOUR_DRAWER_HEIGHT,
    width: '100%',
  },
}));

export function TourDrawer({
  liveViewing = false,
  keepDrawerOpen = false,
  open,
  setOpen,
  drawerRef,
  children,
}: TourDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  let handleRotation = '';
  if (isMobile && open) {
    handleRotation = 'rotateZ(-90deg) translateY(3px)';
  } else if (isMobile && !open) {
    handleRotation = 'rotateZ(90deg) translateY(3px)';
  } else if (open) {
    handleRotation = 'rotateY(180deg) translateY(3px)';
  }

  const isOpen = open || keepDrawerOpen;

  return (
    <>
      <DrawerHandle
        onClick={() => setOpen((prevState) => !prevState)}
        $liveViewing={liveViewing}
        $drawerIsOpen={isOpen}
        $isMobile={isMobile}
        $drawerHeight={
          drawerRef.current?.clientHeight ?? parseInt(TOUR_DRAWER_HEIGHT)
        }
      >
        <Lottie
          animationData={ArrowJSON}
          style={{
            transform: handleRotation,
          }}
        />
      </DrawerHandle>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        variant="persistent"
        SlideProps={{easing: theme.transitions.easing.sharp}}
        transitionDuration={theme.transitions.duration.standard}
        open={isOpen}
        onClose={() => setOpen(false)}
        ModalProps={{keepMounted: true}}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            borderLeft: 'none',
            overflow: 'visible', // Allow handle to show
            boxShadow: 'none',
          },
        }}
      >
        <DrawerContent ref={drawerRef}>{children}</DrawerContent>
      </Drawer>
    </>
  );
}
